import { inject, OnDestroy, Service } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FileProcessType, UserSettings } from '@models';
import {
  AccountService,
  AppEvent,
  AppEventType,
  DiscussionService,
  EventQueueService,
  FileService,
  UserBeanService,
} from '@services';
import type { VoteParent } from '@shared/components/vote/types';
import {
  AppState,
  setIsModalFired,
  setIsModalOpen,
  setIsUserSessionExpiration,
  setNavigationOpen,
  setNavigationRoute,
} from '@store';
import { VoteService } from './vote.service';

@Service()
export class ModalResourceHandlerService implements OnDestroy {
  router = inject(Router);
  store = inject<Store<AppState>>(Store);
  userBeanService = inject(UserBeanService);
  eventQueueService = inject(EventQueueService);
  accountService = inject(AccountService);
  discussionService = inject(DiscussionService);
  fileService = inject(FileService);
  voteService = inject(VoteService);

  destroy$ = new Subject<void>();

  userBean!: UserSettings;
  isLoggedIn!: boolean;
  isModalOpen!: boolean;
  isUserSessionExpiration!: boolean;
  needsRefreshOnDismiss = false;

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initialize = () => {
    this.store.dispatch(
      setNavigationRoute({
        payload: undefined,
      })
    );
    this.store.dispatch(setIsModalOpen({ payload: false }));
    this.store.dispatch(setNavigationOpen({ payload: false }));

    this.store
      .select((state) => state)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.userBean = state.user;
        this.isLoggedIn = state.auth.isLoggedIn;
        this.isModalOpen = state.ui.isModalOpen;
        this.isUserSessionExpiration = state.auth.isUserSessionExpiration;
      });

    this.setupEventSubscriptions();
  };

  setupEventSubscriptions = () => {
    this.eventQueueService
      .listen(AppEventType.OPEN_FILE_LINK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ payload }) => {
        const { rid, processType, isSigning } = payload;

        if (!rid) {
          return;
        }

        if (this.isLoggedIn) {
          this.openFile(rid, processType, isSigning);
        } else {
          this.userBeanService.getUserBean().then(() => {
            this.openFile(rid, processType, isSigning);
          });
        }
      });

    this.eventQueueService
      .listen(AppEventType.OPEN_DISCUSSION_LINK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ payload }) => {
        this.openDiscussion(payload);
      });

    this.eventQueueService
      .listen(AppEventType.OPEN_VOTE_LINK)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ payload }) => {
        const { rid, parentResource } = payload;

        this.openVote(Number(rid), parentResource ?? undefined);
      });

    this.eventQueueService
      .listen(AppEventType.OPEN_DIFFERENT_SESSION_DIALOG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.differentSessionDialog();
      });

    this.eventQueueService
      .listen(AppEventType.USER_SESSION_EXPIRATION)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.userSessionExpiration();
      });

    const markDirtyWhileOpen = () => {
      if (this.isModalOpen) {
        this.needsRefreshOnDismiss = true;
      }
    };

    this.eventQueueService
      .listen(AppEventType.ADD_COMMENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(markDirtyWhileOpen);

    this.eventQueueService
      .listen(AppEventType.ITEM_RENAMED)
      .pipe(takeUntil(this.destroy$))
      .subscribe(markDirtyWhileOpen);

    this.eventQueueService
      .listen(AppEventType.REFRESH_SIGNING_ROOM)
      .pipe(takeUntil(this.destroy$))
      .subscribe(markDirtyWhileOpen);

    this.eventQueueService
      .listen(AppEventType.REFRESH_TREE_LIST)
      .pipe(takeUntil(this.destroy$))
      .subscribe(markDirtyWhileOpen);
  };

  markDirty = () => {
    this.needsRefreshOnDismiss = true;
  };

  openFile = (
    rid: number,
    processType?: FileProcessType,
    isSigning?: boolean
  ) => {
    this.needsRefreshOnDismiss = false;
    this.store.dispatch(setIsModalOpen({ payload: true }));

    this.updateQueryParams({
      file: rid,
      ...(processType && { signprocess: processType }),
    });

    const initialPath = window.location.pathname;

    this.fileService.openFile(rid, processType, isSigning).then(
      () => {
        this.store.dispatch(setIsModalFired({ payload: false }));
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams(['file', 'signprocess']);
          this.refreshData();
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
          );
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_SIGNING_ROOM)
          );
        });
      },
      () => {
        this.store.dispatch(setIsModalFired({ payload: false }));
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams(['file', 'signprocess']);

          if (this.needsRefreshOnDismiss) {
            this.refreshData();
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
            );
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_SIGNING_ROOM)
            );
          }
        });
      }
    );
  };

  openDiscussion = (rid: number) => {
    this.needsRefreshOnDismiss = false;
    this.store.dispatch(setIsModalOpen({ payload: true }));

    this.updateQueryParams({ discussion: rid });

    const initialPath = window.location.pathname;

    this.discussionService.openDiscussion(rid).then(
      () => {
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams('discussion');
          this.refreshData();
        });
      },
      () => {
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams('discussion');

          if (this.needsRefreshOnDismiss) {
            this.refreshData();
          }
        });
      }
    );
  };

  openVote = (rid: number, parent?: VoteParent) => {
    this.needsRefreshOnDismiss = false;
    this.store.dispatch(setIsModalOpen({ payload: true }));

    this.updateQueryParams({ vote: rid });

    const initialPath = window.location.pathname;

    this.voteService.openVote(rid, parent).then(
      () => {
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams('vote');
          this.refreshData();
          this.eventQueueService.dispatch(
            new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
          );
        });
      },
      () => {
        this.afterModalClose(initialPath, () => {
          this.clearQueryParams('vote');

          if (this.needsRefreshOnDismiss) {
            this.refreshData();
            this.eventQueueService.dispatch(
              new AppEvent(AppEventType.REFRESH_EVENT_STRUCTURE)
            );
          }
        });
      }
    );
  };

  afterModalClose = (initialPath: string, onSamePath: () => void) => {
    this.store.dispatch(setIsModalOpen({ payload: false }));

    if (initialPath === window.location.pathname) {
      onSamePath();
    }
  };

  refreshData = () => {
    this.eventQueueService.dispatch(
      new AppEvent(AppEventType.REFRESH_ROOM_LIST)
    );
    this.eventQueueService.dispatch(new AppEvent(AppEventType.REFRESH_RECENT));
  };

  updateQueryParams = (params: Record<string, string | number>) => {
    const { queryParams } =
      this.router.currentNavigation()?.extractedUrl ??
      this.router.parseUrl(this.router.url);
    const isAlreadyInUrl = Object.entries(params).every(
      ([name, value]) => queryParams[name] === String(value)
    );

    if (!isAlreadyInUrl) {
      this.router.navigate([], {
        queryParams: params,
        queryParamsHandling: 'merge',
      });
    }
  };

  clearQueryParams = (paramNames: string | string[]) => {
    if (this.router.currentNavigation()) {
      return;
    }

    const params: Record<string, null> = {};
    const names = Array.isArray(paramNames) ? paramNames : [paramNames];

    names.forEach((name) => {
      params[name] = null;
    });

    this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  };

  userSessionExpiration = () => {
    if (!this.isLoggedIn || !this.userBean) {
      return;
    }

    if (!this.isUserSessionExpiration) {
      this.store.dispatch(setIsUserSessionExpiration({ payload: true }));

      this.accountService.userSessionExpiration().then(
        () => {},
        () => {
          this.store.dispatch(setIsUserSessionExpiration({ payload: false }));
        }
      );
    }
  };

  differentSessionDialog = () => {
    this.accountService.differentSessionDialog();
  };
}
